<template>
    <div class="relative">
        <slot :open="open"></slot>

        <transition
            enter-from-class="opacity-0 -translate-y-4"
            enter-to-class="opacity-100 translate-y-0"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-4"
        >
            <div v-if="opened" class="absolute left-0 top-full z-20 mt-2 w-max transition" @click.stop>
                <vx-date-picker
                    v-on-click-outside="close"
                    mode="date"
                    isRange
                    :modelValue="props.modelValue"
                    :timezone="props.timezone"
                    :minDate="computedMinDate"
                    :maxDate="computedMaxDate"
                    @update:modelValue="applyRange"
                    @dayclick="handleDayClick"
                />
            </div>
        </transition>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue';
import { VxDatePicker } from '@voxie/frontend-components';
import { vOnClickOutside } from '@vueuse/components';
import dayjs from '~/utils/dayjs';

type PickerRange = { start: Date; end: Date };

const props = withDefaults(
    defineProps<{
        modelValue: PickerRange;
        minDate: Date;
        maxDate: Date;
        maxRangeDays?: number | null;
        timezone: string;
    }>(),
    { maxRangeDays: null }
);

const emit = defineEmits<{ 'update:modelValue': [PickerRange] }>();

const opened = ref(false);
const clickedDay = ref<Date | null>(null);

const computedMaxDate = computed(() => {
    if (null === props.maxRangeDays || null === clickedDay.value) {
        return props.maxDate;
    }

    const capped = dayjs(clickedDay.value).add(props.maxRangeDays, 'day').toDate();

    return capped > props.maxDate ? props.maxDate : capped;
});

const computedMinDate = computed(() => {
    if (null === props.maxRangeDays || null === clickedDay.value) {
        return props.minDate;
    }

    const capped = dayjs(clickedDay.value).subtract(props.maxRangeDays, 'day').toDate();

    return capped < props.minDate ? props.minDate : capped;
});

const handleDayClick = (event: { date: Date }): void => {
    if (null === clickedDay.value) {
        clickedDay.value = event.date;
    }
};

const open = (): void => {
    opened.value = true;
};

const close = (): void => {
    opened.value = false;
    clickedDay.value = null;
};

const applyRange = async (range: PickerRange): Promise<void> => {
    emit('update:modelValue', range);

    opened.value = false;

    await nextTick();
    clickedDay.value = null;
};
</script>
